import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Grid, Paper, alpha, useMediaQuery, useTheme } from '@mui/material';

import { parseJsonObject } from '~/utils/societePayloadBuilder/parseHelpers';
import { createDefaultWhatsNewCarouselState } from '~/utils/societePayloadBuilder/whatsNewCarousel/defaults';
import { hasWhatsNewCarouselProgress } from '~/utils/societePayloadBuilder/whatsNewCarousel/hasProgress';
import { parseWhatsNewCarouselPayload } from '~/utils/societePayloadBuilder/whatsNewCarousel/parsePayload';
import type { WhatsNewCarouselPayloadBuilderState } from '~/utils/societePayloadBuilder/whatsNewCarousel/types';
import { validateWhatsNewCarouselPayload } from '~/utils/societePayloadBuilder/whatsNewCarousel/validatePayload';

import { usePayloadBuilderToolbarActions } from '../PayloadBuilderToolbarActions';

import WhatsNewCarouselPayloadActions from './WhatsNewCarouselPayloadActions';
import WhatsNewCarouselPayloadBuilderForm from './WhatsNewCarouselPayloadBuilderForm';
import WhatsNewCarouselPayloadPreview from './WhatsNewCarouselPayloadPreview';

const WORKSPACE_MAX_HEIGHT = 'calc(100vh - 300px)';

interface WhatsNewCarouselPayloadBuilderPageProps {
  onProgressChange?: (hasProgress: boolean) => void;
}

export default function WhatsNewCarouselPayloadBuilderPage({
  onProgressChange,
}: WhatsNewCarouselPayloadBuilderPageProps) {
  const theme = useTheme();
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const isMobileToolbar = useMediaQuery(theme.breakpoints.down('md'));

  const [state, setState] = useState<WhatsNewCarouselPayloadBuilderState>(() => createDefaultWhatsNewCarouselState());
  const [loadJson, setLoadJson] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [collapseResetKey, setCollapseResetKey] = useState(0);
  const [loadCollapseKey, setLoadCollapseKey] = useState(0);

  const validation = useMemo(() => validateWhatsNewCarouselPayload(state), [state]);
  const toolbarActions = useMemo(
    () => <WhatsNewCarouselPayloadActions state={state} isValid={validation.isValid} fullWidth={isMobileToolbar} />,
    [isMobileToolbar, state, validation.isValid],
  );

  usePayloadBuilderToolbarActions(toolbarActions);

  useEffect(() => {
    onProgressChange?.(hasWhatsNewCarouselProgress(state, loadJson));
  }, [loadJson, onProgressChange, state]);

  const applyJson = useCallback((text: string) => {
    try {
      setState(parseWhatsNewCarouselPayload(parseJsonObject(text)));
      setCollapseResetKey((value) => value + 1);
      setLoadCollapseKey((value) => value + 1);
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  }, []);

  const handleLoadPayload = useCallback(() => {
    applyJson(loadJson);
  }, [applyJson, loadJson]);

  const handleUploadFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === 'string' ? reader.result : '';
        setLoadJson(text);
        applyJson(text);
      };
      reader.readAsText(file);
    },
    [applyJson],
  );

  const panelSx = {
    borderRadius: 2,
    border: `1px solid ${borderSoft}`,
    maxHeight: WORKSPACE_MAX_HEIGHT,
    overflow: 'auto',
    boxSizing: 'border-box',
  } as const;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5.4 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
            <WhatsNewCarouselPayloadBuilderForm
              collapseResetKey={collapseResetKey}
              state={state}
              onChange={setState}
              loadJson={loadJson}
              onLoadJsonChange={setLoadJson}
              loadError={loadError}
              loadCollapseKey={loadCollapseKey}
              onLoadPayload={handleLoadPayload}
              onClearLoad={() => {
                setLoadJson('');
                setLoadError(null);
              }}
              onUploadFile={handleUploadFile}
            />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6.6 }}>
          <WhatsNewCarouselPayloadPreview state={state} borderSoft={borderSoft} maxHeight={WORKSPACE_MAX_HEIGHT} />
        </Grid>
      </Grid>
    </Box>
  );
}
